/**
 * Simple Read Cache for GET requests
 * Uses IndexedDB for persistent storage
 */

class SimpleReadCache {
  constructor() {
    this.dbName = 'DestinyRebornCache';
    this.dbVersion = 1;
    this.storeName = 'api-cache';
    this.db = null;
    this.initPromise = this.init();
  }

  async init() {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'key' });
        }
      };
    });
  }

  async ensureReady() {
    if (!this.db) {
      await this.initPromise;
    }
    return this.db;
  }

  /**
   * Get cached data
   * @param {string} key Cache key
   * @returns {Promise<any|null>} Cached data or null if expired/missing
   */
  async get(key) {
    try {
      await this.ensureReady();
      
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      
      return new Promise((resolve) => {
        const request = store.get(key);
        
        request.onsuccess = () => {
          const result = request.result;
          
          if (!result) {
            resolve(null);
            return;
          }

          // Check TTL
          if (result.expiresAt && Date.now() > result.expiresAt) {
            // Expired, delete and return null
            this.del(key).catch(console.warn);
            resolve(null);
            return;
          }

          resolve(result.data);
        };
        
        request.onerror = () => resolve(null);
      });
    } catch (error) {
      console.warn('Cache get error:', error);
      return null;
    }
  }

  /**
   * Set cached data
   * @param {string} key Cache key  
   * @param {any} data Data to cache
   * @param {object} options Options
   * @param {number} options.ttlMs TTL in milliseconds (default: 5min)
   */
  async set(key, data, options = {}) {
    try {
      await this.ensureReady();
      
      const ttlMs = options.ttlMs || (5 * 60 * 1000); // 5 minutes default
      const expiresAt = Date.now() + ttlMs;
      
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      const cacheEntry = {
        key,
        data,
        expiresAt,
        cachedAt: Date.now()
      };
      
      store.put(cacheEntry);
    } catch (error) {
      console.warn('Cache set error:', error);
    }
  }

  /**
   * Delete cached data
   * @param {string} key Cache key
   */
  async del(key) {
    try {
      await this.ensureReady();
      
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      store.delete(key);
    } catch (error) {
      console.warn('Cache delete error:', error);
    }
  }

  /**
   * Prune expired entries
   */
  async prune() {
    try {
      await this.ensureReady();
      
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      const request = store.openCursor();
      const now = Date.now();
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const entry = cursor.value;
          if (entry.expiresAt && now > entry.expiresAt) {
            cursor.delete();
          }
          cursor.continue();
        }
      };
    } catch (error) {
      console.warn('Cache prune error:', error);
    }
  }

  /**
   * Generate cache key for API GET requests
   * @param {string} url Normalized URL
   * @returns {string} Cache key
   */
  static getApiKey(url) {
    return `GET::${url}`;
  }

  /**
   * Clear all cache (for debugging)
   */
  async clear() {
    try {
      await this.ensureReady();
      
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      store.clear();
    } catch (error) {
      console.warn('Cache clear error:', error);
    }
  }
}

// Singleton instance
export { SimpleReadCache };
export const readCache = new SimpleReadCache();

// Auto-prune every 10 minutes
setInterval(() => {
  readCache.prune().catch(() => {
    // Ignore prune errors
  });
}, 10 * 60 * 1000);